/**
 * Block 12 PR-B Phase B3 Step 9 — AnketaForm pure reducer.
 *
 * Each reducer call:
 *   1. `applyActionRaw(state, action)` — атомично применяет действие к одному bucket'у.
 *   2. `recomputeValidation(...)` — pereparse state.params через refined Zod schema
 *      для current anketaType, обновляет `state.validation` (ADR-0020 §Q7).
 *   3. Bumps `updatedAt = Date.now()` для user-driven actions. HYDRATE_FROM_DRAFT и
 *      RESET имеют свою логику updatedAt (preserve / use createdAt).
 *
 * Refined schemas exist для 4 anketa types (kitchen, cabinet, bath-cabinet, balcony)
 * — Part A добавила cross-field invariants. Остальные используют base schemas
 * (Антресоль/Тумба-ванна/ТВ-зона/Другая мебель).
 *
 * Reducer pure — никаких side effects (нет fetch, нет IDB writes, нет Date.now()
 * скрытого в applyActionRaw). Side effects живут в hooks (useAnketaPersist Step 12).
 *
 * OQ-5 (ADR-0020): HYDRATE_FROM_DRAFT может сменить `anketaType`. Reducer
 * recomputes validation после hydration — defensive против IDB corruption / schema
 * drift между app versions.
 */

import {
  BalconyParamsRefinedSchema,
  BathCabinetParamsRefinedSchema,
  BathShelfAboveParamsSchema,
  BathTabletopParamsSchema,
  CabinetParamsRefinedSchema,
  KitchenParamsRefinedSchema,
  OtherParamsSchema,
  TvZoneParamsSchema,
  type CalculatorAnketaType,
} from '@pomoshnik/shared';
import type { AnketaAction } from './actions';
import { assertNever, type AnketaParams, type AnketaState, type FieldError } from '../types';
import { LIGHTING_ROW_SPECS } from '../helpers/lightingRowSpecs';

/**
 * Pick the strictest available Zod schema для anketa type.
 *
 * Refined schemas (kitchen / cabinet / bath-cabinet / balcony) — `ZodEffects<ZodObject>`
 * с cross-field invariants из Part A. Остальные — pure `ZodObject` (Tumba/Antresol/
 * TvZone) или `.strict()` (Other).
 *
 * Return type инферится как union of all schemas — все они имеют `.safeParse(unknown)`
 * (общий для ZodEffects и ZodObject). `apps/web` НЕ зависит от `zod` напрямую:
 * type information пробрасывается через `@pomoshnik/shared` transitively, runtime
 * вызовы (`.safeParse`, `.issues`) — duck typing на union.
 */
function pickRefinedSchema(anketaType: CalculatorAnketaType) {
  switch (anketaType) {
    case 'kitchen':
      return KitchenParamsRefinedSchema;
    case 'cabinet':
      return CabinetParamsRefinedSchema;
    case 'bath-tabletop':
      return BathTabletopParamsSchema;
    case 'bath-shelf-above':
      return BathShelfAboveParamsSchema;
    case 'bath-cabinet':
      return BathCabinetParamsRefinedSchema;
    case 'tv-zone':
      return TvZoneParamsSchema;
    case 'balcony':
      return BalconyParamsRefinedSchema;
    case 'other':
      return OtherParamsSchema;
    default:
      return assertNever(anketaType, 'pickRefinedSchema');
  }
}

/**
 * Phase B4 Step 3 — invariants для Section В additional sub-selections.
 *
 * Currently single rule: `facade-radius` toggle on AND no selection (5 типов
 * методики §2.1 Б.2: пленка модерн / пленка рефленка / эмаль модерн / эмаль
 * рефленка / шпон) → FieldError. Future toggleable additional ids с sub-options
 * могут добавить свои правила в эту функцию.
 *
 * НЕ depends on methodology (если facade-radius toggled — selection нужна
 * независимо от того, в `applicableAdditional` ли она; defensive против
 * methodology drift).
 */
function validateAdditionalSelections(state: AnketaState): FieldError[] {
  const errors: FieldError[] = [];
  if (state.additionalToggles['facade-radius'] && !state.additionalSelections['facade-radius']) {
    errors.push({
      path: 'additionalSelections.facade-radius',
      message: 'Выберите тип радиусного фасада',
    });
  }
  return errors;
}

function recomputeValidation(state: AnketaState): AnketaState {
  const errors: FieldError[] = [];

  const schema = pickRefinedSchema(state.anketaType);
  const parsed = schema.safeParse(state.params);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      errors.push({
        path: issue.path.length > 0 ? issue.path.join('.') : '',
        message: issue.message,
      });
    }
  }

  errors.push(...validateAdditionalSelections(state));

  return {
    ...state,
    validation: { isValid: errors.length === 0, errors },
  };
}

/**
 * Per-type blank params builder. Возвращает только `type` discriminator —
 * остальные поля заполняются user input через `SET_PARAM` actions. TS exhaustive.
 */
function defaultParams(anketaType: CalculatorAnketaType): AnketaParams {
  switch (anketaType) {
    case 'kitchen':
      return { type: 'kitchen' };
    case 'cabinet':
      return { type: 'cabinet' };
    case 'bath-tabletop':
      return { type: 'bath-tabletop' };
    case 'bath-shelf-above':
      return { type: 'bath-shelf-above' };
    case 'bath-cabinet':
      return { type: 'bath-cabinet' };
    case 'tv-zone':
      return { type: 'tv-zone' };
    case 'balcony':
      return { type: 'balcony' };
    case 'other':
      return { type: 'other' };
    default:
      return assertNever(anketaType, 'defaultParams');
  }
}

/**
 * Create blank initial state. `projectId` must be client-generated (ADR-0019 §Q11.3
 * — `genUuid()` in caller, see `@/lib/uuid`). `now` injectable для test determinism.
 */
export function createInitialState(
  anketaType: CalculatorAnketaType,
  projectId: string,
  now: number = Date.now(),
): AnketaState {
  const blank: AnketaState = {
    anketaType,
    projectId,
    projectName: '',
    params: defaultParams(anketaType),
    materialOverrides: {},
    materialPrices: {},
    brandSelections: {},
    additionalToggles: {},
    additionalQuantities: {},
    additionalPrices: {},
    additionalSelections: {},
    additionalLengths: {},
    additionalHeights: {},
    additionalPricesPerM2: {},
    fillingToggles: {},
    fillingQuantities: {},
    fillingPrices: {},
    lightingEnabled: false,
    lightingQuantities: {},
    lightingPrices: {},
    consumableToggles: {},
    consumableQuantities: {},
    consumablePrices: {},
    serviceToggles: {},
    serviceOverrides: {},
    servicePrices: {},
    customRows: [],
    validation: { isValid: false, errors: [] },
    isDirty: false,
    createdAt: now,
    updatedAt: now,
  };
  return recomputeValidation(blank);
}

/**
 * PR-D1 (2026-05-31) — иммутабельно удалить ключ из map. Возвращает ту же ссылку,
 * если ключа нет (no-op, не плодим лишние объекты). Используется кнопкой «Убрать»
 * в toggleable-секциях: при off очищаем введённые Кол-во/Цену/Бренд позиции.
 */
function omitKey<T>(map: Record<string, T>, id: string): Record<string, T> {
  if (!(id in map)) return map;
  const copy = { ...map };
  delete copy[id];
  return copy;
}

function applyActionRaw(state: AnketaState, action: AnketaAction): AnketaState {
  switch (action.type) {
    case 'SET_PARAM': {
      return {
        ...state,
        params: { ...state.params, [action.field]: action.value } as AnketaParams,
        isDirty: true,
      };
    }
    case 'SET_PROJECT_NAME': {
      return { ...state, projectName: action.name, isDirty: true };
    }
    case 'SET_MATERIAL_PRICE': {
      return {
        ...state,
        materialPrices: { ...state.materialPrices, [action.rowId]: action.price },
        isDirty: true,
      };
    }
    case 'OVERRIDE_MATERIAL_QTY': {
      return {
        ...state,
        materialOverrides: { ...state.materialOverrides, [action.rowId]: action.qty },
        isDirty: true,
      };
    }
    case 'RESET_MATERIAL_AUTO': {
      const next = { ...state.materialOverrides };
      delete next[action.rowId];
      return { ...state, materialOverrides: next, isDirty: true };
    }
    case 'SET_BRAND': {
      return {
        ...state,
        brandSelections: { ...state.brandSelections, [action.rowId]: action.brand },
        isDirty: true,
      };
    }
    case 'TOGGLE_ADDITIONAL': {
      if (action.on) {
        return {
          ...state,
          additionalToggles: { ...state.additionalToggles, [action.id]: true },
          isDirty: true,
        };
      }
      // PR-D1 (2026-05-31) — «Убрать» = свернуть + ОЧИСТИТЬ все введённые данные
      // позиции (решение Aibek). Раньше Кол-во/Цена сохранялись → повторное
      // «+ Добавить» показывало старые значения (ghost). Чистим qty/price/selection/
      // length/height/pricePerM2 (vitrine compound) + brand. selection чистился и
      // раньше (Phase B4 Step 3 — иначе ghost в persist freeItems).
      return {
        ...state,
        additionalToggles: { ...state.additionalToggles, [action.id]: false },
        additionalQuantities: omitKey(state.additionalQuantities, action.id),
        additionalPrices: omitKey(state.additionalPrices, action.id),
        additionalSelections: omitKey(state.additionalSelections, action.id),
        additionalLengths: omitKey(state.additionalLengths, action.id),
        additionalHeights: omitKey(state.additionalHeights, action.id),
        additionalPricesPerM2: omitKey(state.additionalPricesPerM2, action.id),
        brandSelections: omitKey(state.brandSelections, action.id),
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_QTY': {
      return {
        ...state,
        additionalQuantities: { ...state.additionalQuantities, [action.id]: action.qty },
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_PRICE': {
      return {
        ...state,
        additionalPrices: { ...state.additionalPrices, [action.id]: action.price },
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_SELECTION': {
      return {
        ...state,
        additionalSelections: {
          ...state.additionalSelections,
          [action.id]: action.value,
        },
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_LENGTH': {
      return {
        ...state,
        additionalLengths: { ...state.additionalLengths, [action.id]: action.length },
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_HEIGHT': {
      return {
        ...state,
        additionalHeights: { ...state.additionalHeights, [action.id]: action.height },
        isDirty: true,
      };
    }
    case 'SET_ADDITIONAL_PRICE_PER_M2': {
      return {
        ...state,
        additionalPricesPerM2: {
          ...state.additionalPricesPerM2,
          [action.id]: action.pricePerM2,
        },
        isDirty: true,
      };
    }
    case 'TOGGLE_FILLING': {
      if (action.on) {
        return {
          ...state,
          fillingToggles: { ...state.fillingToggles, [action.id]: true },
          isDirty: true,
        };
      }
      // PR-D1 — «Убрать» очищает Кол-во/Цену/Бренд позиции наполнения.
      return {
        ...state,
        fillingToggles: { ...state.fillingToggles, [action.id]: false },
        fillingQuantities: omitKey(state.fillingQuantities, action.id),
        fillingPrices: omitKey(state.fillingPrices, action.id),
        brandSelections: omitKey(state.brandSelections, action.id),
        isDirty: true,
      };
    }
    case 'SET_FILLING_QTY': {
      return {
        ...state,
        fillingQuantities: { ...state.fillingQuantities, [action.id]: action.qty },
        isDirty: true,
      };
    }
    case 'SET_FILLING_PRICE': {
      return {
        ...state,
        fillingPrices: { ...state.fillingPrices, [action.id]: action.price },
        isDirty: true,
      };
    }
    case 'TOGGLE_LIGHTING': {
      if (!action.on) {
        // PR-D1 — «Убрать» подсветку (master toggle секции) = выключить + очистить
        // засеянные Кол-во (defaultQty=1), Цены и Бренды строк подсветки. Раньше
        // эти карты сохранялись → повторное включение возвращало старые значения.
        let brand = state.brandSelections;
        for (const spec of LIGHTING_ROW_SPECS) {
          brand = omitKey(brand, spec.id);
        }
        return {
          ...state,
          lightingEnabled: false,
          lightingQuantities: {},
          lightingPrices: {},
          brandSelections: brand,
          isDirty: true,
        };
      }
      // Bug #9 — при включении master toggle seed missing keys в
      // `lightingQuantities` с `defaultQty=1` per методика §1.4.1 (фиксированные
      // quantities). Existing custom values (повторное включение после off)
      // не перезаписываются.
      const seeded = { ...state.lightingQuantities };
      for (const spec of LIGHTING_ROW_SPECS) {
        if (seeded[spec.id] === undefined) {
          seeded[spec.id] = spec.defaultQty;
        }
      }
      return {
        ...state,
        lightingEnabled: true,
        lightingQuantities: seeded,
        isDirty: true,
      };
    }
    case 'SET_LIGHTING_QTY': {
      return {
        ...state,
        lightingQuantities: { ...state.lightingQuantities, [action.rowId]: action.qty },
        isDirty: true,
      };
    }
    case 'SET_LIGHTING_PRICE': {
      return {
        ...state,
        lightingPrices: { ...state.lightingPrices, [action.rowId]: action.price },
        isDirty: true,
      };
    }
    case 'TOGGLE_CONSUMABLE': {
      if (action.on) {
        return {
          ...state,
          consumableToggles: { ...state.consumableToggles, [action.id]: true },
          isDirty: true,
        };
      }
      // PR-D1 — «Убрать» очищает Кол-во/Цену/Бренд позиции расходников.
      return {
        ...state,
        consumableToggles: { ...state.consumableToggles, [action.id]: false },
        consumableQuantities: omitKey(state.consumableQuantities, action.id),
        consumablePrices: omitKey(state.consumablePrices, action.id),
        brandSelections: omitKey(state.brandSelections, action.id),
        isDirty: true,
      };
    }
    case 'SET_CONSUMABLE_QTY': {
      return {
        ...state,
        consumableQuantities: { ...state.consumableQuantities, [action.id]: action.qty },
        isDirty: true,
      };
    }
    case 'SET_CONSUMABLE_PRICE': {
      return {
        ...state,
        consumablePrices: { ...state.consumablePrices, [action.id]: action.price },
        isDirty: true,
      };
    }
    case 'TOGGLE_SERVICE': {
      if (action.on) {
        return {
          ...state,
          serviceToggles: { ...state.serviceToggles, [action.id]: true },
          isDirty: true,
        };
      }
      // PR-D1 — «Убрать» очищает qty-override (→ возврат к авто), Цену и Бренд услуги.
      return {
        ...state,
        serviceToggles: { ...state.serviceToggles, [action.id]: false },
        serviceOverrides: omitKey(state.serviceOverrides, action.id),
        servicePrices: omitKey(state.servicePrices, action.id),
        brandSelections: omitKey(state.brandSelections, action.id),
        isDirty: true,
      };
    }
    case 'OVERRIDE_SERVICE_QTY': {
      return {
        ...state,
        serviceOverrides: { ...state.serviceOverrides, [action.id]: action.qty },
        isDirty: true,
      };
    }
    case 'RESET_SERVICE_AUTO': {
      const next = { ...state.serviceOverrides };
      delete next[action.id];
      return { ...state, serviceOverrides: next, isDirty: true };
    }
    case 'SET_SERVICE_PRICE': {
      return {
        ...state,
        servicePrices: { ...state.servicePrices, [action.id]: action.price },
        isDirty: true,
      };
    }
    case 'ADD_CUSTOM_ROW': {
      return { ...state, customRows: [...state.customRows, action.row], isDirty: true };
    }
    case 'UPDATE_CUSTOM_ROW': {
      return {
        ...state,
        customRows: state.customRows.map((r) =>
          r.id === action.rowId ? { ...r, ...action.patch } : r,
        ),
        isDirty: true,
      };
    }
    case 'REMOVE_CUSTOM_ROW': {
      return {
        ...state,
        customRows: state.customRows.filter((r) => r.id !== action.rowId),
        isDirty: true,
      };
    }
    case 'SET_VALIDATION': {
      return {
        ...state,
        validation: { isValid: action.isValid, errors: action.errors },
      };
    }
    case 'HYDRATE_FROM_DRAFT': {
      // OQ-5 (ADR-0020): hydrated state может иметь другой anketaType.
      // `isDirty: false` потому что state только что загружен из IDB —
      // user не вносил unsaved changes. Validation recomputed выше в anketaReducer().
      //
      // Hotfix-2B Phase B4 Step 9 — defensive backward-compat для новых
      // additionalLengths/Heights/PricesPerM2 maps. Если hydrated state — старая
      // запись IDB без этих полей, fallback на пустые {} (per же pattern что
      // AnketaFormContainer.tsx hydration block использует defaults).
      return {
        ...action.state,
        additionalLengths: action.state.additionalLengths ?? {},
        additionalHeights: action.state.additionalHeights ?? {},
        additionalPricesPerM2: action.state.additionalPricesPerM2 ?? {},
        isDirty: false,
      };
    }
    case 'RESET': {
      // Preserve original `createdAt` (the project was created at some past timestamp),
      // bump `updatedAt` to "now" (the reset itself is a user action). Wrapper-level
      // `if (action.type === 'RESET') return raw;` skips its own Date.now() bump, so
      // RESET sets both timestamps explicitly here.
      const fresh = createInitialState(action.anketaType, action.projectId);
      return { ...fresh, createdAt: state.createdAt };
    }
    default:
      return assertNever(action, 'anketaReducer');
  }
}

/**
 * Reducer entry point. Применяет action → recomputes validation → bumps updatedAt.
 *
 * Спецслучаи:
 *   - RESET — createInitialState уже зовёт recomputeValidation; updatedAt установлен.
 *   - HYDRATE_FROM_DRAFT — preserve hydrated updatedAt; defense-recompute validation.
 *   - SET_VALIDATION — internal action для round-trip из useAnketaForm validate();
 *     не bump'аем updatedAt и не triggerим recompute (loop guard).
 */
export function anketaReducer(state: AnketaState, action: AnketaAction): AnketaState {
  const raw = applyActionRaw(state, action);
  if (action.type === 'RESET') return raw;
  if (action.type === 'SET_VALIDATION') return raw;
  if (action.type === 'HYDRATE_FROM_DRAFT') return recomputeValidation(raw);
  const validated = recomputeValidation(raw);
  return { ...validated, updatedAt: Date.now() };
}
